import { useState } from 'react';
import type { TeamValuation } from '../lib/valuation';

interface Props {
  v: TeamValuation;
  className?: string;
  label?: string;
}

/** Click target showing the suggested $; on hover/click opens a transparent breakdown. */
export function SuggestedValueBadge({ v, className = '', label = 'Suggested' }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <span className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-900/60 border border-teal-700 hover:border-teal-400 text-teal-300 text-[10px] font-bold cursor-help"
      >
        {label} ${v.suggested}
      </button>
      {open && (
        <span className="absolute z-50 left-1/2 -translate-x-1/2 mt-1 w-72 p-3 bg-slate-900 border border-teal-700 rounded-lg shadow-2xl text-xs text-slate-200 normal-case font-normal">
          <div className="font-bold text-teal-300 mb-1">Why ${v.suggested}?</div>
          <Row label="League base / team" value={`$${v.leagueBase.toFixed(2)}`} />
          <Row
            label={`Strength (${labelForSource(v.source)})`}
            value={`${(v.strength * 100).toFixed(0)}% → ×${v.strengthMult.toFixed(2)}`}
          />
          <Row
            label="Last yr price"
            value={v.priorPrice != null
              ? `$${v.priorPrice} vs avail-avg $${v.leagueAvgPrior.toFixed(1)} → ×${v.priorMult.toFixed(2)}`
              : 'n/a → ×1.00'}
          />
          <div className="border-t border-slate-700 mt-2 pt-2 text-slate-400">
            <span className="font-mono">
              ${v.leagueBase.toFixed(2)} × {v.strengthMult.toFixed(2)} × (0.7 + 0.3×{v.priorMult.toFixed(2)})
            </span>
            <div className="text-teal-300 font-bold mt-1">≈ ${v.rawValue.toFixed(2)} → ${v.suggested}</div>
          </div>
        </span>
      )}
    </span>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-400">{label}</span>
      <span className="text-slate-100 font-semibold">{value}</span>
    </div>
  );
}

function labelForSource(s: TeamValuation['source']): string {
  switch (s) {
    case 'fpi': return 'FPI projection';
    case 'record': return 'live record';
    default: return 'neutral';
  }
}
